import React, { useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Button,
  UncontrolledTooltip
} from "reactstrap";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import moment from "moment";
import "./SnowFlakeDashboard.scss";

const SnowFlakeChart = ({
  spikesData,
  attributionData,
  loading,
  trend,
  matchRate
}) => {
  const [filterAccount, setFilterAccount] = useState("All");
  const [visualMode, setVisualMode] = useState("bar"); // bar, line, pie, gauge

  const filteredSpikes = spikesData.filter(s => {
    const accountMatch =
      filterAccount === "All" || s.CONSUMER_ACCOUNT_LOCATOR === filterAccount;
    return accountMatch;
  });

  // ─── Chart Renderers ────────────────────────────────────────────────────
  const renderBarChart = () => {
    if (filteredSpikes.length === 0) {
      return (
        <div className="h-100 d-flex align-items-center justify-content-center text-muted">
          <p>No storage/usage spikes found for the selected period.</p>
        </div>
      );
    }

    return (
      <div className="spikes-bars h-100">
        {filteredSpikes.slice(0, 50).map((row, i) => {
          const maxCredits =
            Math.max(
              ...filteredSpikes.map(r => parseFloat(r.CREDITS_USED || 0))
            ) || 1;
          const rawHeight =
            (parseFloat(row.CREDITS_USED || 0) / maxCredits) * 100;
          const height = Math.max(rawHeight, 5);
          const color =
            rawHeight > 70 ? "#e85d4a" : rawHeight > 40 ? "#f4a261" : "#3a86ff";
          const label = row.LISTING_GLOBAL_NAME
            ? row.LISTING_GLOBAL_NAME.split(".").pop()
            : row.CONSUMER_ACCOUNT_LOCATOR || "–";

          return (
            <div key={i} className="bar-wrapper">
              <div
                className="chart-element"
                style={{
                  height: `${height}%`,
                  background: color,
                  bottom: "0",
                  borderRadius: "6px 6px 0 0",
                  opacity: 1,
                  position: "relative"
                }}
                id={`bar-tooltip-${i}`}
              ></div>
              <UncontrolledTooltip placement="top" target={`bar-tooltip-${i}`}>
                <div className="text-left py-1 px-2">
                  <strong>{label}</strong>
                  <br />
                  <small>
                    Load: {parseFloat(row.CREDITS_USED || 0).toFixed(2)}
                  </small>
                  <br />
                  <small>
                    {moment(row.USAGE_DATE).format("MMM DD, HH:mm")}
                  </small>
                </div>
              </UncontrolledTooltip>
            </div>
          );
        })}
      </div>
    );
  };

  const renderLineChart = () => {
    const data = filteredSpikes.slice(0, 50).reverse();
    if (data.length < 2)
      return (
        <div className="text-center p-4">Not enough data for line chart.</div>
      );

    const maxCredits =
      Math.max(...data.map(r => parseFloat(r.CREDITS_USED || 0))) || 1;
    const width = 1000;
    const height = 180;
    const points = data
      .map((d, i) => {
        const x = (i / (data.length - 1)) * width;
        const y =
          height - (parseFloat(d.CREDITS_USED || 0) / maxCredits) * height;
        return `${x},${y}`;
      })
      .join(" ");

    return (
      <div className="line-chart-wrapper h-100 w-100 d-flex align-items-center">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="w-100 overflow-visible"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="line-gradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3a86ff" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#3a86ff" stopOpacity="0" />
            </linearGradient>
          </defs>
          <polyline
            fill="none"
            stroke="#3a86ff"
            strokeWidth="4"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={points}
          />
          <polygon
            fill="url(#line-gradient)"
            points={`0,${height} ${points} ${width},${height}`}
          />
        </svg>
      </div>
    );
  };

  const renderPieChart = () => {
    const data = [...attributionData]
      .sort((a, b) => b.TOTAL_CREDITS - a.TOTAL_CREDITS)
      .slice(0, 5);
    if (data.length === 0)
      return <div className="p-4 text-center">No attribution data</div>;

    const total = data.reduce(
      (sum, r) => sum + parseFloat(r.TOTAL_CREDITS || 0),
      0
    );
    let currentAngle = 0;
    const colors = ["#3a86ff", "#f4a261", "#e85d4a", "#2ec4b6", "#8338ec"];

    return (
      <div className="pie-chart-wrapper h-100 d-flex align-items-center justify-content-center pt-2">
        <svg
          viewBox="0 0 40 40"
          className="pie-svg h-100"
          style={{ maxHeight: "180px" }}
        >
          {data.map((d, i) => {
            const val = parseFloat(d.TOTAL_CREDITS || 0);
            const slice = (val / total) * 100;
            const offset = 100 - currentAngle;
            currentAngle += slice;

            return (
              <circle
                key={i}
                r="15.915"
                cx="20"
                cy="20"
                fill="transparent"
                stroke={colors[i % colors.length]}
                strokeWidth="6"
                strokeDasharray={`${slice} ${100 - slice}`}
                strokeDashoffset={offset}
                transform="rotate(-90 20 20)"
              />
            );
          })}
          <circle r="12" cx="20" cy="20" fill="white" />
          <text
            x="20"
            y="22"
            textAnchor="middle"
            fontSize="4"
            fontWeight="bold"
            fill="#64748b"
          >
            Credits
          </text>
        </svg>
        <div className="pie-legend ml-4">
          {data.map((d, i) => (
            <div key={i} className="small d-flex align-items-center mb-1">
              <span
                className="dot mr-2"
                style={{
                  background: colors[i % colors.length],
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%"
                }}
              ></span>
              <span className="text-truncate" style={{ maxWidth: "120px" }}>
                {d.CONSUMER_ACCOUNT_LOCATOR}
              </span>
              <span className="ml-2 font-weight-bold">
                {((parseFloat(d.TOTAL_CREDITS) / total) * 100).toFixed(0)}%
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderGaugeChart = () => {
    return (
      <div className="gauge-chart-wrapper h-100 d-flex align-items-center justify-content-around py-2">
        <div className="text-center" style={{ width: 140 }}>
          <CircularProgressbar
            value={matchRate}
            text={`${matchRate}%`}
            styles={buildStyles({
              pathColor: "#8338ec",
              textColor: "#8338ec",
              trailColor: "#f3e8ff",
              strokeLinecap: "round"
            })}
          />
          <p className="mt-2 text-muted small font-weight-bold">
            Correlation Rate
          </p>
        </div>
        <div className="text-center" style={{ width: 140 }}>
          <CircularProgressbar
            value={Math.min(trend.value, 100)}
            text={`${trend.value}%`}
            styles={buildStyles({
              pathColor: trend.isUp ? "#e85d4a" : "#2ec4b6",
              textColor: trend.isUp ? "#e85d4a" : "#2ec4b6",
              trailColor: "#f7fafc"
            })}
          />
          <p className="mt-2 text-muted small font-weight-bold">
            Consumption Trend
          </p>
        </div>
      </div>
    );
  };

  return (
    <Row className="mb-4">
      <Col md="12">
        <Card className="main-chart-card glass-card">
          <CardBody>
            <div
              className="d-flex justify-content-between align-items-center mb-4 chart-controls-wrapper"
              style={{ gap: "20px" }}
            >
              <div
                className="d-flex align-items-center"
                style={{ gap: "20px" }}
              >
                <h5 className="card-title mb-0">Warehouse Load History</h5>
              </div>

              <div
                className="d-flex align-items-center"
                style={{ gap: "20px" }}
              >
                <select
                  className="form-control form-control-sm select-visual-mode"
                  value={visualMode}
                  onChange={e => setVisualMode(e.target.value)}
                  style={{
                    width: "130px",
                    borderRadius: "8px",
                    fontWeight: "600"
                  }}
                >
                  <option value="bar">Bar Chart</option>
                  <option value="line">Line Chart</option>
                  <option value="pie">Pie Chart</option>
                  <option value="gauge">Gauge Chart</option>
                </select>

                <div className="chart-legend d-flex">
                  <div className="legend-item mr-3">
                    <span className="dot bg-blue"></span> Low
                  </div>
                  <div className="legend-item">
                    <span className="dot bg-red"></span> High
                  </div>
                </div>
              </div>
            </div>

            <div className="spikes-chart-container">
              {loading ? (
                <div className="chart-loader">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Analyzing usage spikes...</p>
                </div>
              ) : (
                <div className="chart-viewport h-100">
                  {visualMode === "bar" && renderBarChart()}
                  {visualMode === "line" && renderLineChart()}
                  {visualMode === "pie" && renderPieChart()}
                  {visualMode === "gauge" && renderGaugeChart()}
                </div>
              )}
              <div className="chart-x-axis d-flex justify-content-between mt-2 px-1 text-muted small">
                <span>← Older Data</span>
                <span>Recent Activity →</span>
              </div>
            </div>
            <p className="chart-footer-note mt-3">
              Interactive chart showing top 45 warehouse load spikes. Peak
              height indicates average running queries per warehouse.
            </p>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default SnowFlakeChart;
